import { useState, useEffect, useRef } from "react";
import S from "../styles/styles";
import { Logo, StatCard, PriorityBadge, StatusBadge, EmptyState, categoryIcon } from "./UI";
import TicketDrawer    from "./TicketDrawer";
import TicketForm      from "./TicketForm";
import ContractorsTab  from "./ContractorsTab";
import RentIncreaseTab from "./RentIncreaseTab";
import InvoicingTab    from "./InvoicingTab";
import LeasesTab       from "./LeasesTab";
import DocumentsTab    from "./DocumentsTab";
import { STATUS_STYLES, PRIORITY_STYLES } from "../data/seed";

const NAV_ITEMS = [
  { id: "dashboard",  icon: "▦",  label: "Dashboard"  },
  { id: "tickets",    icon: "🎫", label: "Tickets"    },
  { id: "people",     icon: "👥", label: "People"     },
  { id: "properties", icon: "🏢", label: "Properties" },
  { id: "finance",    icon: "💳", label: "Finance"    },
  { id: "rent",       icon: "📋", label: "BC Forms"   },
  { id: "settings",   icon: "⚙",  label: "Settings"   },
];

const ManagerDashboard = ({
  currentUser, properties, tickets, users,
  contractors = [], payments = [],
  owners = [], invoices = [],
  leases = [], documents = [],
  onAddProperty, onUpdateProperty,
  onGetComments, onAddComment,
  onInviteRenter,
  notices = [], onAddNotice, onUpdateNotice,
  onUpdateStatus, onUpdateTicket, onAddTicket,
  onAddContractor, onPaymentCreated, onAddRentPayment,
  onAddOwner, onAddInvoice, onUpdateInvoice,
  onAddLease, onUpdateLease,
  onAddDocument, onDeleteDocument,
  onSignOut,
}) => {
  const [activeTab,      setActiveTab]      = useState("dashboard");
  const [peopleTab,      setPeopleTab]      = useState("renters");
  const [propTab,        setPropTab]        = useState("properties");
  const [financeTab,     setFinanceTab]     = useState("payments");
  const [isMobile,       setIsMobile]       = useState(window.innerWidth < 768);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showForm,       setShowForm]       = useState(false);
  const [filterStatus,   setFilterStatus]   = useState("ALL");
  const [filterPriority, setFilterPriority] = useState("ALL");
  const [showPropForm,   setShowPropForm]   = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm,     setInviteForm]     = useState({ name:"", email:"", propertyId:"", unit:"" });
  const [inviteStatus,   setInviteStatus]   = useState(null); // null | "sending" | "sent" | "error"
  const [inviteError,    setInviteError]    = useState("");
  const [propForm,       setPropForm]       = useState({ street:"", city:"Vancouver", province:"BC", postalCode:"", units:"1", image:"building" });
  const [editPropId,     setEditPropId]     = useState(null);
  const [editPropUnits,  setEditPropUnits]  = useState({});
  const [paymentTab,     setPaymentTab]     = useState("payouts"); // payouts | rent
  const [showRentForm,   setShowRentForm]   = useState(false);
  const [rentForm,       setRentForm]       = useState({ renterId:"", renterName:"", unit:"", propertyId:"", amount:"", notes:"", period:"" });
  const [bulkProperty,   setBulkProperty]   = useState("");
  const [bulkAmount,     setBulkAmount]     = useState("");
  const [bulkPeriod,     setBulkPeriod]     = useState("");
  const [showBulkForm,   setShowBulkForm]   = useState(false);

  // -- Mobile resize listener -----------------------------------------------
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleNavClick = (id) => {
    setActiveTab(id);
    setMenuOpen(false);
  };

  const SubTabs = ({ tabs, active, setActive }) => (
    <div style={{ ...S.tabRow, marginBottom: 24 }}>
      {tabs.map(({ id, label }) => (
        <button key={id}
          style={{ ...S.tabBtn, ...(active === id ? S.tabBtnActive : {}) }}
          onClick={() => setActive(id)}>
          {label}
        </button>
      ))}
    </div>
  );

  // Auto-generate next reg code based on existing properties
  const nextRegCode = () => {
    const nums = properties
      .map((p) => p.regCode?.match(/PROP-(\d+)/)?.[1])
      .filter(Boolean)
      .map(Number);
    const next = nums.length ? Math.max(...nums) + 1 : 5001;
    return `PROP-${next}`;
  };

  const myProperties = properties.filter((p) => p.companyId === currentUser?.companyId);
  const myTickets    = tickets.filter((t) => myProperties.some((p) => p.id === t.propertyId));
  const myPayouts    = payments.filter((p) => p.type === "contractor_payout");
  const myRent       = payments.filter((p) => p.type === "rent_received");

  const filtered = myTickets.filter((t) => {
    if (filterStatus   !== "ALL" && t.status   !== filterStatus)   return false;
    if (filterPriority !== "ALL" && t.priority !== filterPriority) return false;
    return true;
  });

  const stats = {
    open:       myTickets.filter((t) => t.status === "OPEN").length,
    inProgress: myTickets.filter((t) => t.status === "IN_PROGRESS").length,
    resolved:   myTickets.filter((t) => t.status === "RESOLVED").length,
    critical:   myTickets.filter((t) => t.priority === "CRITICAL").length,
  };

  const TicketRow = ({ t }) => {
    const prop       = properties.find((p) => p.id === t.propertyId);
    const contractor = contractors.find((c) => c.id === t.contractorId);
    return (
      <div
        style={{ ...S.ticketGrid, background: "transparent" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#ffffff04")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        onClick={() => setSelectedTicket(t)}
      >
        <div style={{ fontSize: 11, color: "#5A5570", fontWeight: 700 }}>#{t.id}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 1, display: "flex", gap: 6, alignItems: "center" }}>
            {t.title}
            {t.photos?.length > 0 && <span style={{ fontSize: 11, color: "#9993A8" }}>📷{t.photos.length}</span>}
            {t.paid && <span style={{ fontSize: 10, color: "#22AA55", fontWeight: 700 }}>✓ Paid</span>}
          </div>
          <div style={{ fontSize: 11, color: "#9993A8" }}>Unit {t.unit} . {t.renter} . {t.date}</div>
        </div>
        <div style={{ fontSize: 12, color: "#9993A8" }}>{prop?.name}</div>
        <div style={{ fontSize: 12, color: contractor ? "#F0EDE8" : "#5A5570" }}>
          {contractor ? contractor.name : "Unassigned"}
        </div>
        <PriorityBadge priority={t.priority} />
        <StatusBadge   status={t.status}   />
        <div style={{ fontSize: 18, color: "#5A5570" }}>›</div>
      </div>
    );
  };

  const TableHead = ({ cols }) => (
    <div style={{ ...S.ticketGridHead, gridTemplateColumns: "40px 1fr 140px 130px 100px 110px 36px" }}>
      {(cols || ["#","Issue","Property","Contractor","Priority","Status",""]).map((h) => (
        <div key={h} style={S.headCell}>{h}</div>
      ))}
    </div>
  );

  const currentPeriod = () => {
    const d = new Date();
    return d.toLocaleString("en-CA", { month: "long", year: "numeric" });
  };

  const handleRentSubmit = () => {
    if (!rentForm.amount || !rentForm.renterId) return;
    onAddRentPayment({
      propertyId:  String(rentForm.propertyId),
      renterName:  rentForm.renterName,
      unit:        rentForm.unit,
      amount:      parseFloat(rentForm.amount),
      status:      "paid",
      date:        new Date().toISOString().split("T")[0],
      notes:       rentForm.notes || rentForm.period || currentPeriod() + " Rent",
    });
    setRentForm({ renterId:"", renterName:"", unit:"", propertyId:"", amount:"", notes:"", period:"" });
    setShowRentForm(false);
  };

  const handleBulkRentSubmit = () => {
    if (!bulkProperty || !bulkAmount) return;
    const propRenters = users.filter((u) => u.role === "renter" && String(u.propertyId) === String(bulkProperty));
    propRenters.forEach((renter) => {
      onAddRentPayment({
        propertyId:  String(bulkProperty),
        renterName:  renter.name,
        unit:        renter.unit,
        amount:      parseFloat(bulkAmount),
        status:      "paid",
        date:        new Date().toISOString().split("T")[0],
        notes:       bulkPeriod || currentPeriod() + " Rent",
      });
    });
    setBulkProperty(""); setBulkAmount(""); setBulkPeriod("");
    setShowBulkForm(false);
    setShowRentForm(false);
  };

  // -- Sidebar content (shared between desktop + mobile overlay) ------------
  const SidebarContent = () => (
    <>
      <div style={S.sidebarSection}>Navigation</div>
      {NAV_ITEMS.map((item) => (
        <div
          key={item.id}
          style={{ ...S.sidebarItem, ...(activeTab === item.id ? S.sidebarItemActive : {}) }}
          onClick={() => handleNavClick(item.id)}
        >
          <span style={{ fontSize: 14 }}>{item.icon}</span> {item.label}
        </div>
      ))}
      <div style={{ flex: 1 }} />
      <div style={S.sidebarSection}>Account</div>
      <div style={{ ...S.sidebarItem, marginTop: 4 }}>
        <div style={{ width: 24, height: 24, borderRadius: 99, background: "#E8622A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>
          {currentUser?.name?.[0]?.toUpperCase()}
        </div>
        {currentUser?.name}
      </div>
      <div style={{ ...S.sidebarItem, color: "#5A5570", fontSize: 12 }} onClick={onSignOut}>Sign Out</div>
    </>
  );

  return (
    <div style={S.body}>
      <div style={{ ...S.layout, flexDirection: "column" }}>

        {/* -- MOBILE TOP BAR -- */}
        {isMobile && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", background: "#0F0F14",
            borderBottom: "1px solid #ffffff08", position: "sticky", top: 0, zIndex: 100,
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px" }}>
              prop<span style={{ color: "#E8622A" }}>desk</span>
            </div>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              style={{ background: "none", border: "none", color: "#F0EDE8", fontSize: 22, cursor: "pointer", padding: "4px 8px", lineHeight: 1 }}
            >
              {menuOpen ? "X" : "Menu"}
            </button>
          </div>
        )}

        {/* -- MOBILE SLIDE-IN MENU OVERLAY -- */}
        {isMobile && menuOpen && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setMenuOpen(false)}
              style={{ position: "fixed", inset: 0, background: "#000000aa", zIndex: 200 }}
            />
            {/* Drawer */}
            <div style={{
              position: "fixed", top: 0, left: 0, bottom: 0, width: 260,
              background: "#0F0F14", borderRight: "1px solid #ffffff08",
              zIndex: 201, display: "flex", flexDirection: "column", padding: "0 0 20px",
              animation: "slideIn 0.2s ease",
            }}>
              <div style={{ padding: "20px 20px 14px", fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px", borderBottom: "1px solid #ffffff08", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>prop<span style={{ color: "#E8622A" }}>desk</span></span>
                <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", color: "#9993A8", fontSize: 18, cursor: "pointer" }}>X</button>
              </div>
              <SidebarContent />
            </div>
          </>
        )}

        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* -- DESKTOP SIDEBAR -- */}
          {!isMobile && (
            <div style={S.sidebar}>
              <div style={S.sidebarLogo}>
                prop<span style={S.logoAccent}>desk</span>
                <div style={{ fontSize: 10, color: "#9993A8", fontWeight: 500, marginTop: 2 }}>Vancouver, BC Portfolio</div>
              </div>
              <SidebarContent />
            </div>
          )}

          {/* -- MAIN CONTENT -- */}
          <div style={{ ...S.main, padding: isMobile ? "20px 16px" : "32px 36px", paddingBottom: isMobile ? "80px" : "32px" }}>

          {/* -- DASHBOARD -- */}
          {activeTab === "dashboard" && <>
            <div style={S.pageTitle}>Dashboard</div>
            <div style={S.pageSub}>Vancouver, BC portfolio . {myProperties.length} properties</div>
            <div style={S.statsRow}>
              <StatCard value={stats.open}       label="Open Tickets"      color="#FF2D2D" />
              <StatCard value={stats.inProgress} label="In Progress"       color="#2A7BE8" />
              <StatCard value={stats.resolved}   label="Resolved"          color="#22AA55" />
              <StatCard value={stats.critical}   label="Critical"          color="#FF2D2D" />
              <StatCard value={leases.filter((l) => { const d = new Date(l.endDate) - new Date(); return d > 0 && d < 60*24*60*60*1000; }).length} label="Leases Expiring 60d" color="#FF7A22" />
            </div>
            <div style={S.tableWrap}>
              <div style={S.tableHeader}>
                <div style={S.tableTitle}>Recent Tickets</div>
                <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => { setActiveTab("tickets"); setShowForm(true); }}>
                  + New Ticket
                </button>
              </div>
              <TableHead />
              {myTickets.slice(0, 6).map((t) => <TicketRow key={t.id} t={t} />)}
              {myTickets.length === 0 && <EmptyState icon="🎫" title="No tickets yet" subtitle="Tickets submitted by renters will appear here" />}
            </div>
          </>}

          {/* -- ALL TICKETS -- */}
          {activeTab === "tickets" && <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div style={S.pageTitle}>All Tickets</div>
              <button style={{ ...S.btnPrimary, fontSize: 13, padding: "10px 18px" }} onClick={() => setShowForm(true)}>+ New Ticket</button>
            </div>
            <div style={S.pageSub}>{filtered.length} tickets across {myProperties.length} properties</div>
            <div style={S.tableWrap}>
              <div style={S.filterRow}>
                {["ALL","OPEN","IN_PROGRESS","RESOLVED"].map((s) => (
                  <button key={s} style={{ ...S.filterBtn, ...(filterStatus === s ? S.filterBtnActive : {}) }} onClick={() => setFilterStatus(s)}>
                    {s === "ALL" ? "All Status" : STATUS_STYLES[s]?.label}
                  </button>
                ))}
                <div style={{ width: 1, background: "#ffffff10", margin: "0 4px" }} />
                {["ALL","CRITICAL","HIGH","MEDIUM","LOW"].map((p) => (
                  <button key={p} style={{ ...S.filterBtn, ...(filterPriority === p ? S.filterBtnActive : {}) }} onClick={() => setFilterPriority(p)}>
                    {p === "ALL" ? "All Priority" : PRIORITY_STYLES[p]?.label}
                  </button>
                ))}
              </div>
              <TableHead />
              {filtered.map((t) => <TicketRow key={t.id} t={t} />)}
              {filtered.length === 0 && <EmptyState icon="🔍" title="No tickets match your filters" />}
            </div>
          </>}

          {/* -- PROPERTIES -- */}
          {activeTab === "properties" && (
            <div>
              <SubTabs
                tabs={[
                  { id: "properties", label: `Properties (${myProperties.length})` },
                  { id: "leases",     label: `Leases (${leases.length})`     },
                  { id: "documents",  label: `Documents (${documents.length})` },
                ]}
                active={propTab}
                setActive={setPropTab}
              />
            </div>
          )}
          {activeTab === "properties" && propTab === "properties" && <>
            <div style={S.pageTitle}>Properties</div>
            <div style={S.pageSub}>{myProperties.length} properties in your Vancouver portfolio</div>
            <div style={S.propGrid}>
              {myProperties.map((p) => {
                const pT          = myTickets.filter((t) => t.propertyId === p.id);
                const openC       = pT.filter((t) => t.status === "OPEN").length;
                const critC       = pT.filter((t) => t.priority === "CRITICAL").length;
                const renterCount = users.filter((u) => u.role === "renter" && u.propertyId === p.id).length;
                return (
                  <div key={p.id} style={S.propCard} onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#E8622A55")} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#ffffff08")}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>{{building:"🏢", house:"🏠", apartment:"🏗", condo:"🏙"}[p.image] || "🏢"}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 14 }}>
                      {p.street || p.address}{p.city ? `, ${p.city}` : ""}{p.province ? `, ${p.province}` : ""}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <div style={S.propMetaChip}>{p.units} units</div>
                      <div style={S.propMetaChip}>👥 {renterCount} renters</div>
                      {openC > 0 && <div style={{ ...S.propMetaChip, background: "#FF2D2D18", color: "#FF2D2D" }}>{openC} open</div>}
                      {critC > 0 && <div style={{ ...S.propMetaChip, background: "#FF2D2D33", color: "#FF2D2D", fontWeight: 800 }}>🔴 {critC} critical</div>}
                    </div>
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #ffffff08" }}>
                      <div style={{ fontSize: 11, color: "#5A5570", marginBottom: 6, fontWeight: 700 }}>RENTER REGISTRATION CODE</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#E8622A", background: "#E8622A10", border: "1px solid #E8622A33", borderRadius: 8, padding: "8px 12px", letterSpacing: "0.08em", flex: 1, textAlign: "center" }}>
                          {p.regCode}
                        </div>
                        <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#F0EDE8", fontSize: 12 }} onClick={() => navigator.clipboard.writeText(p.regCode)} title="Copy">
                          📋
                        </button>
                      </div>
                      <div style={{ fontSize: 11, color: "#5A5570", marginTop: 6 }}>Share privately with renters - they enter this code at signup.</div>
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #ffffff08" }}>
                      {editPropId === p.id ? (
                        <div>
                          <div style={{ fontSize: 11, color: "#5A5570", marginBottom: 6, fontWeight: 700 }}>EDIT UNITS</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input
                              type="number" min="1" max="999"
                              style={{ ...S.input, width: 80, textAlign: "center", fontSize: 16, fontWeight: 700 }}
                              value={editPropUnits[p.id] ?? p.units}
                              onChange={(e) => setEditPropUnits((prev) => ({ ...prev, [p.id]: e.target.value }))}
                            />
                            <button style={{ ...S.btnPrimary, fontSize: 12, padding: "6px 14px" }}
                              onClick={async () => {
                                await onUpdateProperty(p.id, { units: editPropUnits[p.id] ?? p.units });
                                setEditPropId(null);
                              }}>Save</button>
                            <button style={{ ...S.btnSecondary, fontSize: 12, padding: "6px 10px" }}
                              onClick={() => setEditPropId(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button style={{ ...S.btnSmall, background: "#ffffff08", color: "#9993A8", fontSize: 11, width: "100%" }}
                          onClick={() => { setEditPropId(p.id); setEditPropUnits((prev) => ({ ...prev, [p.id]: p.units })); }}>
                          Edit Units
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {!showPropForm ? (
                <div style={{ ...S.propCard, border: "2px dashed #ffffff15", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: 180, cursor: "pointer" }}
                  onClick={() => { setShowPropForm(true); setPropForm((f) => ({ ...f, regCode: nextRegCode() })); }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>+</div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Add Property</div>
                  <div style={{ fontSize: 12, color: "#9993A8", marginTop: 4 }}>Expand your portfolio</div>
                </div>
              ) : (
                <div style={{ ...S.propCard, border: "1px solid #E8622A44", gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>New Property</div>
                  <div style={S.field}>
                    <label style={S.label}>Street Address *</label>
                    <input style={S.input} placeholder="e.g. 3477 E 48th Ave" value={propForm.street}
                      onChange={(e) => setPropForm((f) => ({ ...f, street: e.target.value }))} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div style={S.field}>
                      <label style={S.label}>City *</label>
                      <input style={S.input} placeholder="Vancouver" value={propForm.city}
                        onChange={(e) => setPropForm((f) => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div style={S.field}>
                      <label style={S.label}>Province *</label>
                      <select style={S.select} value={propForm.province} onChange={(e) => setPropForm((f) => ({ ...f, province: e.target.value }))}>
                        {["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"].map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    <div style={S.field}>
                      <label style={S.label}>Postal Code</label>
                      <input style={S.input} placeholder="V5P 1A1" value={propForm.postalCode}
                        onChange={(e) => setPropForm((f) => ({ ...f, postalCode: e.target.value.toUpperCase() }))} />
                    </div>
                    <div style={S.field}>
                      <label style={S.label}>Units</label>
                      <input style={S.input} type="number" min="1" placeholder="6" value={propForm.units}
                        onChange={(e) => setPropForm((f) => ({ ...f, units: e.target.value }))} />
                    </div>
                    <div style={S.field}>
                      <label style={S.label}>Reg Code (auto)</label>
                      <input style={{ ...S.input, color: "#E8622A", fontWeight: 700, letterSpacing: "0.06em" }}
                        value={propForm.regCode} readOnly />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 14px", flex: 1 }}
                      onClick={async () => {
                        if (!propForm.street || !propForm.city) return;
                        await onAddProperty({ ...propForm, name: propForm.street });
                        setPropForm({ street:"", city:"Vancouver", province:"BC", postalCode:"", units:"1", image:"building", regCode: nextRegCode() });
                        setShowPropForm(false);
                      }}>
                      Save Property
                    </button>
                    <button style={{ ...S.btnSecondary, fontSize: 12, padding: "8px 14px" }}
                      onClick={() => setShowPropForm(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>}

          {/* -- RENTERS -- */}
          {activeTab === "people" && (
            <div>
              <SubTabs
                tabs={[
                  { id: "renters",     label: `Renters (${users.filter((u) => u.role === "renter").length})`   },
                  { id: "owners",      label: `Owners (${owners.length})`      },
                  { id: "contractors", label: `Contractors (${contractors.length})` },
                ]}
                active={peopleTab}
                setActive={setPeopleTab}
              />
            </div>
          )}
          {activeTab === "people" && peopleTab === "renters" && <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
              <div>
                <div style={S.pageTitle}>Renters</div>
                <div style={S.pageSub}>{users.filter((u) => u.role === "renter").length} registered renters</div>
              </div>
              <button style={{ ...S.btnPrimary, fontSize: 13, padding: "10px 18px" }} onClick={() => { setShowInviteForm(true); setInviteStatus(null); setInviteError(""); }}>
                + Invite Renter
              </button>
            </div>

            {/* Invite Form */}
            {showInviteForm && (
              <div style={{ background: "#141418", border: "1px solid #E8622A44", borderRadius: 14, padding: 24, marginBottom: 24 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Invite a Renter</div>
                <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 16 }}>They will receive an email with a link to set their password and access the portal.</div>

                {inviteStatus === "sent" ? (
                  <div style={{ background: "#22AA5518", border: "1px solid #22AA5544", borderRadius: 10, padding: "16px 20px", textAlign: "center" }}>
                    <div style={{ fontSize: 20, marginBottom: 8 }}>Invitation Sent!</div>
                    <div style={{ fontSize: 13, color: "#22AA55", fontWeight: 700, marginBottom: 4 }}>{inviteForm.email}</div>
                    <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 16 }}>They will receive an email to set up their account.</div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                      <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => { setInviteForm({ name:"", email:"", propertyId:"", unit:"" }); setInviteStatus(null); }}>
                        Invite Another
                      </button>
                      <button style={{ ...S.btnSecondary, fontSize: 12, padding: "8px 16px" }} onClick={() => setShowInviteForm(false)}>
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                      <div style={S.field}>
                        <label style={S.label}>Full Name *</label>
                        <input style={S.input} placeholder="e.g. John Smith" value={inviteForm.name}
                          onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div style={S.field}>
                        <label style={S.label}>Email Address *</label>
                        <input style={S.input} type="email" placeholder="renter@email.com" value={inviteForm.email}
                          onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                      <div style={S.field}>
                        <label style={S.label}>Property *</label>
                        <select style={S.select} value={inviteForm.propertyId}
                          onChange={(e) => setInviteForm((f) => ({ ...f, propertyId: e.target.value, unit: "" }))}>
                          <option value="">-- Select Property --</option>
                          {myProperties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
                        </select>
                      </div>
                      <div style={S.field}>
                        <label style={S.label}>Unit *</label>
                        <select style={S.select} value={inviteForm.unit}
                          onChange={(e) => setInviteForm((f) => ({ ...f, unit: e.target.value }))}>
                          <option value="">-- Select Unit --</option>
                          {inviteForm.propertyId && Array.from(
                            { length: myProperties.find((p) => String(p.id) === String(inviteForm.propertyId))?.units || 1 },
                            (_, i) => <option key={i+1} value={String(i+1)}>Unit {i+1}</option>
                          )}
                        </select>
                      </div>
                    </div>
                    {inviteError && <div style={{ ...S.errorBox, marginBottom: 12 }}>{inviteError}</div>}
                    <div style={{ ...S.infoBanner, marginBottom: 14 }}>
                      <span>📧</span>
                      <span style={{ fontSize: 12 }}>An email will be sent to the renter with a link to set their password. They do not need a registration code.</span>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        style={{ ...S.btnPrimary, padding: "10px 20px", fontSize: 13, opacity: inviteStatus === "sending" ? 0.6 : 1 }}
                        disabled={inviteStatus === "sending"}
                        onClick={async () => {
                          if (!inviteForm.name || !inviteForm.email || !inviteForm.propertyId || !inviteForm.unit) {
                            setInviteError("Please fill in all fields");
                            return;
                          }
                          setInviteStatus("sending");
                          setInviteError("");
                          const result = await onInviteRenter({
                            name:       inviteForm.name,
                            email:      inviteForm.email,
                            propertyId: parseInt(inviteForm.propertyId),
                            unit:       inviteForm.unit,
                          });
                          if (result.success) {
                            setInviteStatus("sent");
                          } else {
                            setInviteStatus("error");
                            setInviteError(result.error || "Failed to send invite. Please try again.");
                          }
                        }}>
                        {inviteStatus === "sending" ? "Sending..." : "Send Invite"}
                      </button>
                      <button style={{ ...S.btnSecondary, padding: "10px 20px", fontSize: 13 }}
                        onClick={() => { setShowInviteForm(false); setInviteStatus(null); setInviteError(""); }}>
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={S.tableWrap}>
              {/* Desktop header -- hidden on mobile */}
              {!isMobile && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 110px 80px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
                  {["Name","Email","Property / Unit","Status","Tickets"].map((h) => <div key={h} style={S.headCell}>{h}</div>)}
                </div>
              )}
              {users.filter((u) => u.role === "renter").map((u) => {
                const prop = properties.find((p) => p.id === u.propertyId);
                const tc   = tickets.filter((t) => t.renterId === u.id).length;

                // All self-registered renters are active
                const badge = "Active", badgeColor = "#22AA55", badgeBg = "#22AA5518";

                // Mobile card layout
                if (isMobile) return (
                  <div key={u.id} style={{ padding: "14px 16px", borderBottom: "1px solid #ffffff06" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 99, background: "#E8622A22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#E8622A", fontWeight: 800, flexShrink: 0 }}>{u.name?.[0] || "?"}</div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700 }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: "#9993A8" }}>{u.email}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: badgeBg, color: badgeColor, flexShrink: 0 }}>{badge}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>
                        {prop ? `${prop.address || prop.name} · Unit ${u.unit}` : "No property"}
                      </div>
                      <div style={{ fontSize: 12, color: tc > 0 ? "#E8622A" : "#5A5570", fontWeight: 700 }}>
                        {tc} ticket{tc !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                );

                // Desktop row layout
                return (
                  <div key={u.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 110px 80px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 99, background: "#E8622A22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#E8622A", fontWeight: 800 }}>{u.name?.[0] || "?"}</div>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{u.name}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>{u.email}</div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>{prop ? `${prop.address || prop.name} / Unit ${u.unit}` : "-"}</div>
                    <div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: badgeBg, color: badgeColor }}>{badge}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: tc > 0 ? "#E8622A" : "#9993A8" }}>{tc}</div>
                  </div>
                );
              })}
              {users.filter((u) => u.role === "renter").length === 0 && <EmptyState icon="👥" title="No renters yet" subtitle="Use the Invite Renter button above to add tenants" />}
            </div>
          </>}

          {/* -- CONTRACTORS -- */}
          {activeTab === "people" && peopleTab === "contractors" && (
            <ContractorsTab
              contractors={contractors}
              tickets={myTickets}
              payments={payments}
              onAddContractor={onAddContractor}
            />
          )}

          {/* -- PAYMENTS -- */}
          {activeTab === "finance" && (
            <div>
              <SubTabs
                tabs={[
                  { id: "payments",  label: "Payments"  },
                  { id: "invoicing", label: "Invoicing" },
                ]}
                active={financeTab}
                setActive={setFinanceTab}
              />
            </div>
          )}
          {activeTab === "finance" && financeTab === "payments" && <>
            <div style={S.pageTitle}>Payments</div>
            <div style={S.pageSub}>Contractor payouts and rent received</div>

            {/* Summary */}
            <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
              {[
                { label: "Total Paid Out",  value: `$${myPayouts.filter((p)=>p.status==="paid").reduce((s,p)=>s+p.amount,0).toLocaleString()}`, color:"#635BFF" },
                { label: "Rent Received",   value: `$${myRent.filter((p)=>p.status==="paid").reduce((s,p)=>s+p.amount,0).toLocaleString()}`,    color:"#22AA55" },
                { label: "Pending Payouts", value: myPayouts.filter((p)=>p.status==="pending").length,                                           color:"#FF7A22" },
              ].map((st) => (
                <div key={st.label} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "14px 18px", minWidth: 160 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.value}</div>
                  <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 2 }}>{st.label}</div>
                </div>
              ))}
            </div>

            {/* Sub-tabs */}
            <div style={{ ...S.tabRow, maxWidth: 320, marginBottom: 20 }}>
              {[["payouts","💳 Contractor Payouts"],["rent","🏠 Rent Received"]].map(([id, label]) => (
                <button key={id} style={{ ...S.tabBtn, ...(paymentTab === id ? S.tabBtnActive : {}) }} onClick={() => setPaymentTab(id)}>
                  {label}
                </button>
              ))}
            </div>

            {/* Contractor Payouts */}
            {paymentTab === "payouts" && (
              <div style={S.tableWrap}>
                <div style={S.tableHeader}>
                  <div style={S.tableTitle}>Contractor Payouts</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 100px 100px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
                  {["Date","Contractor","Job","Amount","Status"].map((h) => <div key={h} style={S.headCell}>{h}</div>)}
                </div>
                {myPayouts.map((p) => {
                  const contractor = contractors.find((c) => c.id === p.contractorId);
                  return (
                    <div key={p.id} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 100px 100px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>{p.date}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{contractor?.name || "-"}</div>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>{p.notes}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#F0EDE8" }}>${p.amount.toFixed(2)}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: p.status === "paid" ? "#22AA5518" : "#FF7A2218", color: p.status === "paid" ? "#22AA55" : "#FF7A22" }}>
                        {p.status === "paid" ? "✓ Paid" : "Pending"}
                      </span>
                    </div>
                  );
                })}
                {myPayouts.length === 0 && <EmptyState icon="💳" title="No payouts yet" subtitle="Pay contractors from ticket drawer when job is resolved" />}
              </div>
            )}

            {/* Rent Received */}
            {paymentTab === "rent" && (
              <div style={S.tableWrap}>
                <div style={S.tableHeader}>
                  <div style={S.tableTitle}>Rent Received</div>
                  <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => setShowRentForm(true)}>+ Log Payment</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr 100px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
                  {["Date","Renter","Property . Unit","Notes","Amount"].map((h) => <div key={h} style={S.headCell}>{h}</div>)}
                </div>
                {myRent.map((p) => {
                  const prop = properties.find((pr) => pr.id === p.propertyId);
                  return (
                    <div key={p.id} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr 100px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>{p.date}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{p.renterName}</div>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>{prop?.name || "-"}{p.unit ? ` . Unit ${p.unit}` : ""}</div>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>{p.notes}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#22AA55" }}>${p.amount.toFixed(2)}</div>
                    </div>
                  );
                })}
                {myRent.length === 0 && <EmptyState icon="🏠" title="No rent logged yet" subtitle="Use the Log Payment button above to record rent received" />}
              </div>
            )}
          </>}

          {/* -- RENT INCREASE -- */}
          {activeTab === "rent" && (
            <RentIncreaseTab
              properties={properties}
              users={users}
              notices={notices}
              onAddNotice={onAddNotice}
              onUpdateNotice={onUpdateNotice}
            />
          )}

          {/* -- OWNERS -- */}
          {activeTab === "people" && peopleTab === "owners" && (
            <div>
              <div style={S.pageTitle}>Property Owners</div>
              <div style={S.pageSub}>Manage owner contacts, management fees and billing</div>

              {/* Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px,1fr))", gap: 14, marginBottom: 28 }}>
                {[
                  { label: "Total Owners",    value: owners.length,                                                             color: "#2A7BE8" },
                  { label: "Monthly Revenue", value: `$${owners.reduce((s, o) => s + (o.mgmtFee || 0), 0).toLocaleString()}`, color: "#22AA55" },
                  { label: "Properties",      value: myProperties.length,                                                       color: "#E8622A" },
                ].map((st) => (
                  <div key={st.label} style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: st.color }}>{st.value}</div>
                    <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 4 }}>{st.label}</div>
                  </div>
                ))}
              </div>

              {/* Owner cards */}
              {owners.length === 0 ? (
                <div style={{ padding: "48px 24px", textAlign: "center" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🏠</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No owners yet</div>
                  <div style={{ fontSize: 13, color: "#9993A8", marginBottom: 16 }}>Add owners in the Invoicing tab</div>
                  <button style={{ ...S.btnPrimary, fontSize: 13, padding: "10px 20px" }} onClick={() => { setActiveTab("finance"); setFinanceTab("invoicing"); }}>
                    Go to Invoicing
                  </button>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: 16 }}>
                  {owners.map((o) => {
                    const prop = myProperties.find((p) => p.id === o.propertyId);
                    const ownerInvoices = invoices.filter((i) => i.ownerId === o.id);
                    const totalBilled   = ownerInvoices.reduce((s, i) => s + (i.total || 0), 0);
                    const unpaid        = ownerInvoices.filter((i) => i.status !== "Paid").reduce((s, i) => s + (i.total || 0), 0);
                    return (
                      <div key={o.id} style={{ ...S.propCard, cursor: "default" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                          <div style={{ width: 44, height: 44, borderRadius: 99, background: "#2A7BE818", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: "#2A7BE8" }}>
                            {o.name?.[0] || "O"}
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>{o.name}</div>
                            <div style={{ fontSize: 12, color: "#9993A8" }}>{prop?.address || prop?.name || "No property"}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 4 }}>📧 {o.email || "-"}</div>
                        <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 14 }}>📞 {o.phone || "-"}</div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                          {o.mgmtFee > 0 && <div style={S.propMetaChip}>${o.mgmtFee}/mo mgmt fee</div>}
                          {totalBilled > 0 && <div style={{ ...S.propMetaChip, background: "#2A7BE818", color: "#2A7BE8" }}>${totalBilled.toLocaleString()} billed</div>}
                          {unpaid > 0 && <div style={{ ...S.propMetaChip, background: "#FF2D2D18", color: "#FF2D2D" }}>${unpaid.toLocaleString()} outstanding</div>}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {o.email && (
                            <button style={{ ...S.btnSmall, background: "#2A7BE818", color: "#2A7BE8", flex: 1 }}
                              onClick={() => window.open(`mailto:${o.email}`, "_blank")}>
                              Email Owner
                            </button>
                          )}
                          <button style={{ ...S.btnSmall, background: "#E8622A18", color: "#E8622A", flex: 1 }}
                            onClick={() => { setActiveTab("finance"); setFinanceTab("invoicing"); }}>
                            View Invoices
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* -- LEASES -- */}
          {activeTab === "properties" && propTab === "leases" && (
            <LeasesTab
              leases={leases}
              properties={myProperties}
              users={users}
              onAddLease={onAddLease}
              onUpdateLease={onUpdateLease}
            />
          )}

          {/* -- DOCUMENTS -- */}
          {activeTab === "properties" && propTab === "documents" && (
            <DocumentsTab
              documents={documents}
              properties={myProperties}
              users={users}
              onAddDocument={onAddDocument}
              onDeleteDocument={onDeleteDocument}
            />
          )}

          {/* -- INVOICING -- */}
          {activeTab === "finance" && financeTab === "invoicing" && (
            <InvoicingTab
              properties={properties}
              payments={payments}
              owners={owners}
              invoices={invoices}
              onAddOwner={onAddOwner}
              onAddInvoice={onAddInvoice}
              onUpdateInvoice={onUpdateInvoice}
            />
          )}

          {/* -- SETTINGS -- */}
          {activeTab === "settings" && <>
            <div style={S.pageTitle}>Settings</div>
            <div style={S.pageSub}>Manage your workspace and billing</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {[
                { title: "Company Profile",         icon: "🏢", desc: "Name, logo, and contact information" },
                { title: "Team Members",             icon: "👥", desc: "Invite and manage staff access" },
                { title: "Billing & Plan",           icon: "💳", desc: "Current plan: Pro . Manage subscription" },
                { title: "Notification Preferences", icon: "🔔", desc: "Email alerts for new and urgent tickets" },
                { title: "Integrations",             icon: "🔗", desc: "Connect Slack, email, and SMS alerts" },
                { title: "Renter Portal Branding",   icon: "🎨", desc: "Custom colors and logo for your tenants" },
              ].map((c) => (
                <div key={c.title} style={{ ...S.propCard, cursor: "pointer" }}>
                  <div style={{ fontSize: 24, marginBottom: 10 }}>{c.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </>}

        </div>
        </div>

      {/* Ticket drawer */}
      {selectedTicket && (
        <TicketDrawer
          ticket={selectedTicket}
          properties={properties}
          contractors={contractors}
          owners={owners}
          users={users}
          onGetComments={onGetComments}
          onAddComment={onAddComment}
          currentUser={currentUser}
          onClose={() => setSelectedTicket(null)}
          onUpdateStatus={(id, status) => {
            onUpdateStatus(id, status);
            setSelectedTicket((prev) => ({ ...prev, status }));
          }}
          onUpdateTicket={(id, updates) => {
            onUpdateTicket(id, updates);
            setSelectedTicket((prev) => ({ ...prev, ...updates }));
          }}
          onPaymentCreated={onPaymentCreated}
        />
      )}

      {/* New ticket form */}
      {showForm && (
        <TicketForm
          currentUser={currentUser}
          properties={myProperties}
          tickets={tickets}
          onSubmit={onAddTicket}
          onClose={() => setShowForm(false)}
          isManager={true}
        />
      )}

      {/* Log rent payment form */}
      {showRentForm && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setShowRentForm(false)}>
          <div style={{ ...S.drawer, maxWidth: 520 }}>
            <div style={S.drawerTitle}>Post Rent Payment</div>
            <div style={S.drawerSub}>Select a renter to post their monthly rent receipt.</div>

            {/* Toggle between single and bulk */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              <button
                style={{ ...S.tabBtn, ...(showBulkForm ? {} : S.tabBtnActive) }}
                onClick={() => setShowBulkForm(false)}
              >
                Single Renter
              </button>
              <button
                style={{ ...S.tabBtn, ...(showBulkForm ? S.tabBtnActive : {}) }}
                onClick={() => setShowBulkForm(true)}
              >
                All Renters (Bulk)
              </button>
            </div>

            {!showBulkForm ? (
              <>
                {/* Select renter — auto-fills everything */}
                <div style={S.field}>
                  <label style={S.label}>Select Renter *</label>
                  <select
                    style={S.select}
                    value={rentForm.renterId}
                    onChange={(e) => {
                      const renter = users.find((u) => String(u.id) === e.target.value);
                      if (renter) {
                        setRentForm((p) => ({
                          ...p,
                          renterId:   String(renter.id),
                          renterName: renter.name,
                          unit:       renter.unit || "",
                          propertyId: String(renter.propertyId || ""),
                        }));
                      } else {
                        setRentForm((p) => ({ ...p, renterId: "", renterName: "", unit: "", propertyId: "" }));
                      }
                    }}
                  >
                    <option value="">-- Select renter --</option>
                    {users.filter((u) => u.role === "renter").map((u) => {
                      const prop = myProperties.find((p) => String(p.id) === String(u.propertyId));
                      return (
                        <option key={u.id} value={u.id}>
                          {u.name} — Unit {u.unit}{prop ? ` · ${prop.address}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Auto-filled info preview */}
                {rentForm.renterName && (
                  <div style={{ background: "#22AA5510", border: "1px solid #22AA5530", borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#22AA55", marginBottom: 6 }}>Posting rent for:</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{rentForm.renterName}</div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>
                      Unit {rentForm.unit} &nbsp;·&nbsp; {myProperties.find((p) => String(p.id) === String(rentForm.propertyId))?.address || ""}
                    </div>
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div style={S.field}>
                    <label style={S.label}>Amount (CAD) *</label>
                    <input
                      style={S.input}
                      type="number"
                      placeholder="e.g. 2200.00"
                      value={rentForm.amount}
                      onChange={(e) => setRentForm((p) => ({ ...p, amount: e.target.value }))}
                    />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Period</label>
                    <input
                      style={S.input}
                      placeholder={currentPeriod() + " Rent"}
                      value={rentForm.period}
                      onChange={(e) => setRentForm((p) => ({ ...p, period: e.target.value }))}
                    />
                  </div>
                </div>

                <div style={S.field}>
                  <label style={S.label}>Notes (optional)</label>
                  <input
                    style={S.input}
                    placeholder="e.g. Paid via e-Transfer"
                    value={rentForm.notes}
                    onChange={(e) => setRentForm((p) => ({ ...p, notes: e.target.value }))}
                  />
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button
                    style={{ ...S.btnPrimary, flex: 1, opacity: (rentForm.renterId && rentForm.amount) ? 1 : 0.4 }}
                    onClick={handleRentSubmit}
                    disabled={!rentForm.renterId || !rentForm.amount}
                  >
                    Post Rent Receipt
                  </button>
                  <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#F0EDE8" }} onClick={() => setShowRentForm(false)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                {/* Bulk post for all renters at a property */}
                <div style={{ background: "#2A7BE810", border: "1px solid #2A7BE830", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#2A7BE8", marginBottom: 4 }}>Bulk Post</div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>Posts the same rent amount for every renter at the selected property at once.</div>
                </div>

                <div style={S.field}>
                  <label style={S.label}>Property *</label>
                  <select style={S.select} value={bulkProperty} onChange={(e) => setBulkProperty(e.target.value)}>
                    <option value="">-- Select property --</option>
                    {myProperties.map((p) => {
                      const count = users.filter((u) => u.role === "renter" && String(u.propertyId) === String(p.id)).length;
                      return <option key={p.id} value={p.id}>{p.address} ({count} renters)</option>;
                    })}
                  </select>
                </div>

                {bulkProperty && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: "#9993A8", marginBottom: 6 }}>Will post for:</div>
                    {users.filter((u) => u.role === "renter" && String(u.propertyId) === String(bulkProperty)).map((u) => (
                      <div key={u.id} style={{ fontSize: 12, color: "#F0EDE8", padding: "3px 0" }}>
                        {u.name} — Unit {u.unit}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div style={S.field}>
                    <label style={S.label}>Amount per unit (CAD) *</label>
                    <input style={S.input} type="number" placeholder="e.g. 2200.00" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} />
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Period</label>
                    <input style={S.input} placeholder={currentPeriod() + " Rent"} value={bulkPeriod} onChange={(e) => setBulkPeriod(e.target.value)} />
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button
                    style={{ ...S.btnPrimary, flex: 1, opacity: (bulkProperty && bulkAmount) ? 1 : 0.4 }}
                    onClick={handleBulkRentSubmit}
                    disabled={!bulkProperty || !bulkAmount}
                  >
                    Post for All Renters
                  </button>
                  <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#F0EDE8" }} onClick={() => setShowRentForm(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

        {/* -- MOBILE BOTTOM NAV -- */}
        {isMobile && (
          <div style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            background: "#0F0F14", borderTop: "1px solid #ffffff10",
            display: "flex", zIndex: 100, paddingBottom: "env(safe-area-inset-bottom)",
          }}>
            {NAV_ITEMS.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                style={{
                  flex: 1, background: "none", border: "none", padding: "10px 4px 8px",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                  cursor: "pointer",
                  color: activeTab === item.id ? "#E8622A" : "#5A5570",
                }}
              >
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.03em" }}>
                  {item.label.split(" ")[0]}
                </span>
              </button>
            ))}
            {/* More button for remaining nav items */}
            <button
              onClick={() => setMenuOpen(true)}
              style={{
                flex: 1, background: "none", border: "none", padding: "10px 4px 8px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                cursor: "pointer", color: "#5A5570",
              }}
            >
              <span style={{ fontSize: 18 }}>{"..."}</span>
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.03em" }}>More</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;
